import { api, type MailDraft } from './api'
import {
  isOfflinePurged,
  isStorable,
  mailOfflineFactory,
  type OfflineBackend,
  type OfflineBackendFactory,
  offlineDbName,
  offlinePurgedError,
  type ProtectionSnapshot,
} from './mail-offline-store'
import { isNetworkError } from './online-status'

// Outbox for Ember Mail (#785): a mail sent while offline (or hit by a network error)
// waits in IndexedDB (store 'outbox' of the user's offline database) and goes out
// exactly once when the connection is back:
// - the account is fixed at enqueue time; flush always sends via entry.accountId,
// - the idempotency key is created once per entry and never renewed (the server
//   answers a repeated key with `duplicate` instead of a second SMTP delivery),
// - only one sender per user (Web Locks across tabs, in-module mutex as fallback),
//   and every entry is re-read right before it is sent.
// Protected accounts/folders (#524) never get an entry; an account that became
// protected in the meantime gets a server draft in the same account instead.
// A mail composed from a saved server draft carries its id in mail.draftId: the
// queue never deletes that draft; it goes only after the mail went out (also on
// a `duplicate` answer), and a diverted entry updates it instead of adding one.
// The sender identity (#839) is part of the mail (mail.identityId) and goes out
// with it unchanged. When it was deleted or disabled meanwhile, the server answers
// 404 („Absender nicht gefunden“) or 409 MAIL_IDENTITY_DISABLED: both are no
// transient errors, so the entry turns 'fehler' and is never retried, and never
// sent without identityId (that would be another sender).

/** Includes the optional draftId of the saved server draft the mail comes from. */
export type OutboxMail = Parameters<typeof api.sendMail>[1]

/** 'entwurf' = cancelled or diverted: becomes a server draft when online, never sent. */
export type OutboxStatus = 'wartet' | 'fehler' | 'entwurf'

export interface OutboxEntry {
  id: string
  userId: string
  accountId: string
  idempotencyKey: string
  mail: OutboxMail
  createdAt: string
  status: OutboxStatus
  fehlerCode?: string
}

export class OutboxProtectedError extends Error {
  readonly code = 'OUTBOX_PROTECTED'
  constructor() {
    super('Geschütztes Konto oder Ordner: Mail kann nicht im Ausgang gespeichert werden')
    this.name = 'OutboxProtectedError'
  }
}

export interface OutboxDeps {
  userId: string
  factory: OfflineBackendFactory
  sendMail: (accountId: string, mail: OutboxMail, idempotencyKey: string) => Promise<unknown>
  /** Server draft in accountId; updates mail.draftId when that draft still exists. */
  saveDraft: (accountId: string, mail: OutboxMail) => Promise<unknown>
  /** Best effort after a send: removes the server draft the mail came from. */
  deleteDraft?: (draftId: string) => Promise<unknown>
  /** After a draft save whose answer was lost: true when an identical draft of
   * this mail already exists in the account (then no second one is created). */
  findDraft?: (accountId: string, mail: OutboxMail) => Promise<boolean>
  loadAccounts: () => Promise<
    ReadonlyArray<{ id: string; protected: boolean; disabledAt: string | null }>
  >
  /** Default: navigator.locks.request when available, else an in-module mutex by name. */
  withLock?: <T>(name: string, fn: () => Promise<T>) => Promise<T>
  now?: () => Date
  /** Default crypto.randomUUID (v4). */
  uuid?: () => string
}

export interface FlushResult {
  sent: Array<{ id: string; accountId: string; subject: string; sentAt: string }>
  drafted: number
  failed: number
  aborted?: 'offline' | 'unauthorized'
}

export interface MailOutbox {
  /** Throws OutboxProtectedError (nothing stored) unless the account is known
   * unprotected and, for a reply/forward, the source folder is storable. Keeps a
   * given idempotencyKey (online attempt that failed with a network error). */
  enqueue(
    input: {
      accountId: string
      mail: OutboxMail
      sourceFolder?: { accountId: string; path: string }
      idempotencyKey?: string
    },
    snapshot: ProtectionSnapshot | null | undefined,
  ): Promise<OutboxEntry>
  /** This user's entries, oldest first. */
  list(): Promise<OutboxEntry[]>
  /** „Rückgängig“: returns the entry and deletes it (null when already gone/sent). */
  remove(id: string): Promise<OutboxEntry | null>
  /** „Bearbeiten“: same accountId, SAME idempotencyKey, status back to 'wartet'.
   * With a snapshot (also null) the entry's account must be known unprotected,
   * else OutboxProtectedError and nothing is written (#524). */
  update(
    id: string,
    mail: OutboxMail,
    snapshot?: ProtectionSnapshot | null,
  ): Promise<OutboxEntry | null>
  /** „Abbrechen“: server draft in entry.accountId, then deleted → 'draft'; on a
   * network error the entry stays as 'entwurf' (saved as draft by the next flush)
   * → 'pending'. Throws when the entry is gone (e.g. already sent). */
  cancelToDraft(id: string): Promise<'draft' | 'pending'>
  flush(): Promise<FlushResult>
  /** Fires after every change (for UI refresh). */
  subscribe(listener: () => void): () => void
}

export const OUTBOX_LOCK_PREFIX = 'ember-mail-outbox:'

/** 409 MAIL_SEND_IN_PROGRESS answers after which an entry turns 'fehler' (the
 * server row is stuck `pending`, e.g. after a crash mid-send) … */
export const OUTBOX_IN_PROGRESS_LIMIT = 3
/** … once at least this long after the first such answer, or always after
 * OUTBOX_IN_PROGRESS_MAX_MS. A real parallel send is over long before. */
export const OUTBOX_IN_PROGRESS_MIN_MS = 5 * 60 * 1000
export const OUTBOX_IN_PROGRESS_MAX_MS = 60 * 60 * 1000

const STORE = 'outbox'

/** Stored record: the entry plus an insertion sequence for a stable order. */
interface OutboxRecord extends OutboxEntry {
  seq: number
  /** Consecutive MAIL_SEND_IN_PROGRESS answers and the time of the first one. */
  inProgress?: { count: number; since: string }
  /** A draft save was started; its answer may have been lost. */
  draftAttempted?: boolean
}

// ---------------------------------------------------------------------------
// Helpers

function randomUuid(): string {
  const c = typeof crypto === 'undefined' ? undefined : crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // Fallback (insecure context): RFC 4122 v4 from getRandomValues.
  const b = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b)
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256)
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

// Monotonic within this module (two enqueues in the same millisecond keep their order).
let lastSeq = 0
function nextSeq(): number {
  lastSeq = Math.max(Date.now(), lastSeq + 1)
  return lastSeq
}

const localLocks = new Map<string, Promise<void>>()

/** Serialises by name inside this module (fallback without Web Locks). */
function localMutex<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const prev = localLocks.get(name) ?? Promise.resolve()
  const run = prev.then(fn)
  const tail = run.then(
    () => undefined,
    () => undefined,
  )
  localLocks.set(name, tail)
  void tail.then(() => {
    if (localLocks.get(name) === tail) localLocks.delete(name)
  })
  return run
}

function defaultWithLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const locks =
    typeof navigator === 'undefined'
      ? undefined
      : (navigator as { locks?: Pick<LockManager, 'request'> }).locks
  if (locks && typeof locks.request === 'function') {
    // The callback's promise is what the lock waits for; typed as a nested promise.
    const held: Promise<Promise<T>> = locks.request(name, { mode: 'exclusive' }, () => fn())
    return held.then((inner) => inner)
  }
  return localMutex(name, fn)
}

function statusOf(err: unknown): number | undefined {
  const s = (err as { status?: unknown } | null)?.status
  return typeof s === 'number' ? s : undefined
}

function codeOf(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code
  if (typeof code === 'string' && code) return code
  const status = statusOf(err)
  return status === undefined ? 'UNKNOWN' : `HTTP_${status}`
}

/** Temporary server-side trouble: keep 'wartet', retry on the next flush. */
function isTransient(err: unknown): boolean {
  const status = statusOf(err)
  if (status === undefined) return false
  if (status === 409 && codeOf(err) === 'MAIL_SEND_IN_PROGRESS') return true
  return status >= 500 || status === 408 || status === 429
}

function toEntry(rec: OutboxRecord): OutboxEntry {
  const entry: OutboxEntry = {
    id: rec.id,
    userId: rec.userId,
    accountId: rec.accountId,
    idempotencyKey: rec.idempotencyKey,
    mail: rec.mail,
    createdAt: rec.createdAt,
    status: rec.status,
  }
  if (rec.fehlerCode !== undefined) entry.fehlerCode = rec.fehlerCode
  return entry
}

function isRecord(v: unknown): v is OutboxRecord {
  if (v === null || typeof v !== 'object') return false
  const r = v as Record<string, unknown>
  return (
    typeof r.id === 'string' &&
    typeof r.userId === 'string' &&
    typeof r.accountId === 'string' &&
    typeof r.idempotencyKey === 'string' &&
    typeof r.createdAt === 'string' &&
    (r.status === 'wartet' || r.status === 'fehler' || r.status === 'entwurf') &&
    r.mail !== null &&
    typeof r.mail === 'object'
  )
}

// ---------------------------------------------------------------------------
// Outbox

export function createMailOutbox(deps: OutboxDeps): MailOutbox {
  const { userId, factory } = deps
  const dbName = offlineDbName(userId)
  const lockName = OUTBOX_LOCK_PREFIX + userId
  const withLock = deps.withLock ?? defaultWithLock
  const now = deps.now ?? (() => new Date())
  const uuid = deps.uuid ?? randomUuid
  const listeners = new Set<() => void>()

  function notify(): void {
    for (const listener of [...listeners]) {
      try {
        listener()
      } catch {
        // a broken listener must not stop the others
      }
    }
  }

  // A short-lived connection per operation: nothing stays open that could block a
  // purge (deleteDatabase) from logout or another tab.
  async function withBackend<T>(fn: (b: OfflineBackend) => Promise<T>): Promise<T> {
    // Opening would recreate a database that logout/user switch just deleted.
    if (isOfflinePurged(userId)) throw offlinePurgedError()
    const backend = await factory.open(dbName)
    try {
      return await fn(backend)
    } finally {
      backend.close()
    }
  }

  /** Own record by id; a record of another user is deleted and treated as missing. */
  async function readOwn(b: OfflineBackend, id: string): Promise<OutboxRecord | null> {
    const raw = await b.get<unknown>(STORE, id)
    if (raw === undefined) return null
    if (!isRecord(raw) || raw.userId !== userId || raw.id !== id) {
      await b.delete(STORE, id)
      return null
    }
    return raw
  }

  async function listRecords(b: OfflineBackend): Promise<OutboxRecord[]> {
    const out: OutboxRecord[] = []
    for (const e of await b.entries<unknown>(STORE)) {
      const v = e.value
      if (!isRecord(v) || v.userId !== userId || v.id !== e.key) {
        await b.delete(STORE, e.key)
        continue
      }
      out.push(v)
    }
    return out.sort(
      (a, b) =>
        (Number(a.seq) || 0) - (Number(b.seq) || 0) || a.createdAt.localeCompare(b.createdAt),
    )
  }

  function patchRecord(
    rec: OutboxRecord,
    change: Partial<
      Pick<OutboxRecord, 'status' | 'fehlerCode' | 'mail' | 'inProgress' | 'draftAttempted'>
    >,
  ): OutboxRecord {
    const next: OutboxRecord = { ...rec, ...change }
    if (next.status !== 'fehler') delete next.fehlerCode
    if (next.inProgress === undefined) delete next.inProgress
    if (next.draftAttempted === undefined) delete next.draftAttempted
    return next
  }

  // Each step is its own short transaction; no connection stays open while a
  // request is on the network.
  const readEntry = (id: string) => withBackend((b) => readOwn(b, id))
  const writeEntry = (rec: OutboxRecord) => withBackend((b) => b.put(STORE, rec.id, rec))
  const deleteEntry = (id: string) => withBackend((b) => b.delete(STORE, id))

  /** Sets a status only if the entry still exists (fresh read). */
  async function markEntry(
    id: string,
    change: Partial<Pick<OutboxRecord, 'status' | 'fehlerCode' | 'inProgress' | 'draftAttempted'>>,
  ): Promise<void> {
    const fresh = await readEntry(id)
    if (fresh) await writeEntry(patchRecord(fresh, change))
  }

  type Step = 'next' | 'stop'

  /** Server draft of an entry (never sent). Marks the attempt first: when the
   * answer of an earlier attempt was lost, an identical draft that already exists
   * is taken instead of creating a second one. */
  async function saveEntryDraft(entry: OutboxRecord): Promise<void> {
    if (entry.draftAttempted && deps.findDraft) {
      if (await deps.findDraft(entry.accountId, entry.mail)) return
    } else if (!entry.draftAttempted) {
      await markEntry(entry.id, { draftAttempted: true })
    }
    await deps.saveDraft(entry.accountId, entry.mail)
  }

  /** The mail went out (or had already): its server draft goes, best effort. */
  async function dropSentDraft(entry: OutboxRecord): Promise<void> {
    const draftId = entry.mail.draftId
    if (!draftId || !deps.deleteDraft) return
    try {
      await deps.deleteDraft(draftId)
    } catch {
      // housekeeping only; the mail is sent
    }
  }

  /** 409 MAIL_SEND_IN_PROGRESS: keep waiting, unless the server row looks stuck. */
  async function noteInProgress(entry: OutboxRecord, result: FlushResult): Promise<void> {
    const nowMs = now().getTime()
    const previous = entry.inProgress
    const sinceMs = previous ? Date.parse(previous.since) : nowMs
    const count = (previous?.count ?? 0) + 1
    const elapsed = Number.isNaN(sinceMs) ? 0 : nowMs - sinceMs
    if (
      (count >= OUTBOX_IN_PROGRESS_LIMIT && elapsed >= OUTBOX_IN_PROGRESS_MIN_MS) ||
      elapsed >= OUTBOX_IN_PROGRESS_MAX_MS
    ) {
      await markEntry(entry.id, {
        status: 'fehler',
        fehlerCode: 'MAIL_SEND_IN_PROGRESS',
        inProgress: undefined,
      })
      result.failed++
      return
    }
    await markEntry(entry.id, {
      inProgress: { count, since: previous?.since ?? new Date(nowMs).toISOString() },
    })
  }

  async function processEntry(
    id: string,
    accounts: Awaited<ReturnType<OutboxDeps['loadAccounts']>>,
    result: FlushResult,
  ): Promise<Step> {
    // Fresh read right before acting: another tab or an „Rückgängig“ may have
    // removed or changed it in the meantime.
    const entry = await readEntry(id)
    if (!entry) return 'next'
    const account = accounts.find((a) => a.id === entry.accountId)

    // Never sent from a (meanwhile) protected account; cancelled entries become
    // drafts. Also covers 'fehler' entries of an account that is protected now.
    if (account && (account.protected || entry.status === 'entwurf')) {
      try {
        await saveEntryDraft(entry)
      } catch (err) {
        if (isNetworkError(err) || statusOf(err) === 401) {
          if (entry.status !== 'entwurf') await markEntry(id, { status: 'entwurf' })
          result.aborted = statusOf(err) === 401 ? 'unauthorized' : 'offline'
          return 'stop'
        }
        await markEntry(id, { status: 'fehler', fehlerCode: codeOf(err) })
        result.failed++
        return 'next'
      }
      await deleteEntry(id)
      result.drafted++
      return 'next'
    }

    if (entry.status === 'fehler') return 'next' // no automatic retry
    if (!account) {
      await markEntry(id, { status: 'fehler', fehlerCode: 'MAIL_ACCOUNT_MISSING' })
      result.failed++
      return 'next'
    }
    if (account.disabledAt) {
      await markEntry(id, { status: 'fehler', fehlerCode: 'MAIL_ACCOUNT_DISABLED' })
      result.failed++
      return 'next'
    }

    try {
      // Always the stored account and the stored key, never the active account.
      await deps.sendMail(entry.accountId, entry.mail, entry.idempotencyKey)
    } catch (err) {
      if (statusOf(err) === 401) {
        result.aborted = 'unauthorized'
        return 'stop'
      }
      if (isNetworkError(err)) {
        result.aborted = 'offline'
        return 'stop'
      }
      if (statusOf(err) === 409 && codeOf(err) === 'MAIL_SEND_IN_PROGRESS') {
        await noteInProgress(entry, result)
        return 'next'
      }
      if (isTransient(err)) return 'next'
      await markEntry(id, { status: 'fehler', fehlerCode: codeOf(err) })
      result.failed++
      return 'next'
    }
    await deleteEntry(id)
    await dropSentDraft(entry)
    result.sent.push({
      id: entry.id,
      accountId: entry.accountId,
      subject: entry.mail.subject,
      sentAt: now().toISOString(),
    })
    return 'next'
  }

  async function flushLocked(): Promise<FlushResult> {
    const result: FlushResult = { sent: [], drafted: 0, failed: 0 }
    const pending = await withBackend(listRecords)
    if (pending.length === 0) return result
    try {
      let accounts: Awaited<ReturnType<OutboxDeps['loadAccounts']>>
      try {
        accounts = await deps.loadAccounts()
      } catch (err) {
        if (statusOf(err) === 401) result.aborted = 'unauthorized'
        else if (isNetworkError(err)) result.aborted = 'offline'
        else throw err
        return result
      }
      for (const listed of pending) {
        if ((await processEntry(listed.id, accounts, result)) === 'stop') break
      }
      return result
    } finally {
      // Statuses may have changed even without a sent/drafted/failed count.
      notify()
    }
  }

  function missingError(): Error {
    return Object.assign(new Error('Mail ist nicht mehr im Ausgang'), {
      code: 'OUTBOX_ENTRY_MISSING',
    })
  }

  return {
    async enqueue(input, snapshot) {
      const account = snapshot?.accounts.find((a) => a.id === input.accountId)
      if (!snapshot || !account || account.protected !== false) throw new OutboxProtectedError()
      const src = input.sourceFolder
      if (src && !isStorable(snapshot, src.accountId, src.path)) throw new OutboxProtectedError()

      const rec: OutboxRecord = {
        id: randomUuid(),
        userId,
        accountId: input.accountId,
        idempotencyKey: input.idempotencyKey ?? uuid(),
        mail: input.mail,
        createdAt: now().toISOString(),
        status: 'wartet',
        seq: nextSeq(),
      }
      await withBackend((b) => b.put(STORE, rec.id, rec))
      notify()
      return toEntry(rec)
    },

    async list() {
      return (await withBackend(listRecords)).map(toEntry)
    },

    // remove/update/cancelToDraft take the sender lock: an entry that is being sent
    // right now is either sent (then gone → null) or untouched, never both.
    remove(id) {
      return withLock(lockName, async () => {
        const rec = await withBackend(async (b) => {
          const found = await readOwn(b, id)
          if (found) await b.delete(STORE, id)
          return found
        })
        if (!rec) return null
        notify()
        return toEntry(rec)
      })
    },

    update(id, mail, snapshot) {
      return withLock(lockName, async () => {
        const rec = await withBackend(async (b) => {
          const found = await readOwn(b, id)
          if (!found) return null
          if (snapshot !== undefined) {
            const account = snapshot?.accounts.find((a) => a.id === found.accountId)
            if (!account || account.protected !== false) throw new OutboxProtectedError()
          }
          const next = patchRecord(found, {
            mail,
            status: 'wartet',
            inProgress: undefined,
            draftAttempted: undefined,
          })
          await b.put(STORE, id, next)
          return next
        })
        if (!rec) return null
        notify()
        return toEntry(rec)
      })
    },

    cancelToDraft(id) {
      return withLock(lockName, async () => {
        const rec = await readEntry(id)
        if (!rec) throw missingError()
        try {
          await saveEntryDraft(rec)
        } catch (err) {
          // Never sent any more; the next flush saves it as a draft.
          if (rec.status !== 'entwurf') await markEntry(id, { status: 'entwurf' })
          notify()
          if (isNetworkError(err) || statusOf(err) === 401) return 'pending' as const
          throw err
        }
        await deleteEntry(id)
        notify()
        return 'draft' as const
      })
    },

    flush() {
      return withLock(lockName, flushLocked)
    },

    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

// ---------------------------------------------------------------------------
// App wiring

/** OutboxMail → the server draft shape (same account, nothing is sent). The
 * draft the mail came from is updated (the server inserts when it is gone).
 * The source mail goes along (#830): a forward has no header ids, so without
 * it the server could not refuse sending the draft through another account. */
export function outboxMailToDraft(accountId: string, mail: OutboxMail) {
  return {
    id: mail.draftId ?? null,
    accountId,
    toAddrs: mail.to,
    ccAddrs: mail.cc ?? '',
    bccAddrs: mail.bcc ?? '',
    subject: mail.subject,
    bodyText: mail.forwardIntro ?? mail.text ?? '',
    autoAttachSignature: mail.autoAttachSignature ?? true,
    bodyHtml: mail.html ?? '',
    forwardQuoteHtml: mail.forwardQuoteHtml ?? '',
    forwardQuoteText: mail.forwardQuoteText ?? '',
    forwardRemovedIds: mail.forwardRemovedIds ?? [],
    inReplyTo: mail.inReplyTo ?? null,
    references: mail.references ?? [],
    attachments: mail.attachments ?? [],
    ...(mail.sourceMessageId ? { sourceMessageId: mail.sourceMessageId } : {}),
    ...(mail.identityId ? { identityId: mail.identityId } : {}),
  }
}

/** Server draft of an outbox entry (nothing is sent). When the server refuses
 * exactly the identity — deleted → 404, disabled → 409 MAIL_IDENTITY_DISABLED —
 * the draft is saved once more without it and then carries the account default,
 * like a stored draft after its identity is deleted (#839). The send path never
 * drops the identity (that would be another sender). */
export async function saveOutboxDraft(
  save: (draft: ReturnType<typeof outboxMailToDraft>) => Promise<unknown>,
  accountId: string,
  mail: OutboxMail,
): Promise<void> {
  const draft = outboxMailToDraft(accountId, mail)
  try {
    await save(draft)
  } catch (err) {
    const status = statusOf(err)
    const identityRefused =
      draft.identityId !== undefined &&
      (status === 404 || (status === 409 && codeOf(err) === 'MAIL_IDENTITY_DISABLED'))
    if (!identityRefused) throw err
    const { identityId: _dropped, ...withoutIdentity } = draft
    await save(withoutIdentity)
  }
}

/** Same content as the draft this mail would become (lost-answer retry). */
export function sameDraft(
  draft: Pick<
    MailDraft,
    | 'accountId'
    | 'toAddrs'
    | 'ccAddrs'
    | 'bccAddrs'
    | 'subject'
    | 'bodyText'
    | 'bodyHtml'
    | 'forwardQuoteHtml'
    | 'forwardQuoteText'
    | 'forwardRemovedIds'
    | 'inReplyTo'
    | 'attachmentsJson'
  > &
    Partial<
      Pick<MailDraft, 'sourceMessageId' | 'identityId' | 'references' | 'autoAttachSignature'>
    >,
  accountId: string,
  mail: OutboxMail,
): boolean {
  const want = outboxMailToDraft(accountId, mail)
  const files = (list: unknown): string =>
    JSON.stringify(
      (Array.isArray(list) ? list : []).map((item: unknown) =>
        item && typeof item === 'object' && !Array.isArray(item)
          ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)))
          : item,
      ),
    )
  return (
    draft.accountId === want.accountId &&
    draft.toAddrs === want.toAddrs &&
    draft.ccAddrs === want.ccAddrs &&
    draft.bccAddrs === want.bccAddrs &&
    draft.subject === want.subject &&
    draft.bodyText === want.bodyText &&
    (draft.autoAttachSignature ?? true) === want.autoAttachSignature &&
    (draft.bodyHtml ?? '') === want.bodyHtml &&
    (draft.forwardQuoteHtml ?? '') === want.forwardQuoteHtml &&
    (draft.forwardQuoteText ?? '') === want.forwardQuoteText &&
    JSON.stringify(draft.forwardRemovedIds ?? []) === JSON.stringify(want.forwardRemovedIds) &&
    (draft.inReplyTo ?? null) === want.inReplyTo &&
    (draft.sourceMessageId ?? null) === (want.sourceMessageId ?? null) &&
    (draft.identityId ?? null) === (want.identityId ?? null) &&
    JSON.stringify(draft.references ?? []) === JSON.stringify(want.references) &&
    files(draft.attachmentsJson) === files(want.attachments)
  )
}

/** Hint under a fixed sender in the compose dialog: a protected source (#535), a
 * protected draft (#830), or a mail restored from/edited in the outbox (#785) —
 * that one keeps the account it was queued for, which need not be protected. */
export function senderLockHint(opts: { lockedAccount: boolean; draftLocked: boolean }): string {
  if (opts.lockedAccount) return 'Geschütztes Konto: Antwort nur aus diesem Konto'
  if (opts.draftLocked) return 'Geschütztes Konto: Entwurf nur aus diesem Konto senden'
  return 'Aus dem Ausgang: gesendet wird über dieses Konto'
}

const outboxes = new Map<string, MailOutbox>()

/** One outbox per user for the app (IndexedDB + the real API). */
export function getMailOutbox(userId: string): MailOutbox {
  let outbox = outboxes.get(userId)
  if (!outbox) {
    outbox = createMailOutbox({
      userId,
      factory: mailOfflineFactory(),
      sendMail: (accountId, mail, key) => api.sendMail(accountId, mail, key),
      saveDraft: (accountId, mail) => saveOutboxDraft(api.saveMailDraft, accountId, mail),
      deleteDraft: (draftId) => api.deleteMailDraft(draftId),
      findDraft: async (accountId, mail) =>
        (await api.listMailDrafts(accountId)).drafts.some((d) => sameDraft(d, accountId, mail)),
      loadAccounts: async () => (await api.listMailAccounts()).accounts,
    })
    outboxes.set(userId, outbox)
  }
  return outbox
}
